import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LockOutlined } from '@ant-design/icons';
import { Form, Input, message, Modal } from 'antd';
import { LangText } from '@/components';
import { useRootStore } from '@/contexts';
import { apiBuyerModify, BuyerModifyRequest } from '@/service/api';
import apiFileUpload from '@/service/api/fileUpload';
import { preFixImgUrl } from '@/utils';
import EditPasswordForm from './components/EditPasswordForm';
import './index.scss';

const PROFILE_CONTENT_TEXT_BG = preFixImgUrl('7516e41169e457fb78c22a4ca6e60f6b.jpg', false);
const PROFILE_CONTENT_BG = preFixImgUrl('73b30cd2c3511884814c21e9619cc961.png', false);
const PROFILE_CONTENT_BG_START = 380;

const Profile = () => {
  const { t } = useTranslation();
  const { userStore } = useRootStore();
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const [isEditPassword, setIsEditPassword] = useState(false);
  const [form] = Form.useForm<{
    realName: string;
    nickname: string;
    email: string;
    mobile: string;
  }>();

  useEffect(() => {
    if (userStore.user) {
      form.setFieldsValue({
        realName: userStore.user.realName,
        nickname: userStore.user.nickname,
        email: userStore.user.email,
        mobile: userStore.user.mobile,
      });
    }
  }, [userStore.user, form]);

  const handleEdit = () => {
    if (isEdit) {
      form.resetFields();
      setImageUrl(undefined);
    }
    setIsEdit(!isEdit);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const modifyData: BuyerModifyRequest = {
        buyerId: userStore.user?.buyerId,
        realName: values.realName,
        nickname: values.nickname,
        email: values.email,
        mobile: values.mobile,
        headimgurl: imageUrl,
      };

      const res = await apiBuyerModify(modifyData);

      if (res.code === 0) {
        message.success(t('profile.message.saveSuccess'));
        await userStore.getUser();
        setIsEdit(false);
      } else {
        message.error(res.msg || t('profile.message.saveFailed'));
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('保存个人信息失败:', error);
      message.error(error?.msg || t('profile.message.saveUnexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (e: any) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiFileUpload(formData);
    if (res.code === 0) {
      setImageUrl(res.data);
    } else {
      message.error(res.msg || t('profile.message.saveFailed'));
    }
  };

  const avatarSrc = imageUrl || preFixImgUrl(userStore.user?.headimgurl ?? '585a3f702bf0be02a45f07f520f474b3.png');

  return (
    <div
      className="profile-page min-w-[1200px] bg-[#FAFAFA] pb-[80px] pt-[42px]"
      style={{
        backgroundImage: `url(${PROFILE_CONTENT_TEXT_BG}), url(${PROFILE_CONTENT_BG})`,
        backgroundPosition: `top center, center ${PROFILE_CONTENT_BG_START}px`,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundSize: '1920px auto, 100% auto',
      }}
    >
      <div className="mx-auto w-[1200px]">
        <LangText lngKey="profile.title" className="block text-[30px] font-bold leading-[42px] text-[#333]" />

        <section className="mt-[42px]">
          <div className="mb-[24px]">
            <LangText lngKey="profile.personal.title" className="block text-[28px] font-bold leading-[40px] text-[#333]" />
            <LangText lngKey="profile.personal.subtitle" className="mt-[12px] block text-[16px] leading-[22px] text-[#666]" />
          </div>

          <div className="rounded-[20px] bg-white px-[32px] pt-[32px] pb-[42px]">
            <div className="flex items-center justify-between border-b border-[#D9D9D9] pb-[24px] mb-[24px]">
              <div className="flex items-center gap-[12px]">
                <div className="relative h-[74px] w-[74px] overflow-hidden rounded-full border border-[#DDE5EF] bg-[#F2F7FF]">
                  <img draggable={false} src={avatarSrc} alt={t('profile.personal.avatar')} className="object-cover w-full h-full" />
                  {isEdit && (
                    <input
                      type="file"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      accept="image/*"
                      onChange={handleChange}
                    />
                  )}
                </div>
                <LangText lngKey="profile.personal.avatar" className="text-[20px] font-bold leading-[28px] text-[#333]" />
              </div>

              {isEdit ? (
                <div className="flex items-center gap-[28px] text-[20px] leading-[28px]">
                  <LangText
                    lngKey="common.action.cancel"
                    className="cursor-pointer text-[#666]"
                    onClick={handleEdit}
                  />
                  <LangText
                    lngKey={loading ? 'common.status.saving' : 'common.action.save'}
                    className={`cursor-pointer text-[#328BF8] ${loading ? 'cursor-not-allowed opacity-60' : ''}`}
                    onClick={loading ? undefined : handleSave}
                  />
                </div>
              ) : (
                <LangText
                  lngKey="common.action.edit"
                  className="cursor-pointer text-[20px] font-bold leading-[28px] text-[#328BF8]"
                  onClick={handleEdit}
                />
              )}
            </div>

            <Form
              className="profile-info-form"
              disabled={!isEdit}
              form={form}
              initialValues={{
                nickname: userStore.user?.nickname,
                email: userStore.user?.email,
                mobile: userStore.user?.mobile,
              }}
              layout="vertical"
            >
              <div className="grid grid-cols-2 gap-x-[14px] gap-y-[8px]">
                <Form.Item
                  name="nickname"
                  label={<LangText lngKey="profile.form.nickname" />}
                  rules={[{ required: false, message: t('profile.validation.nicknameRequired') }]}
                >
                  <Input placeholder={t('profile.placeholder.nickname')} />
                </Form.Item>

                <Form.Item
                  name="email"
                  label={<LangText lngKey="common.form.email" />}
                  rules={[{ required: false }, { type: 'email', message: t('auth.validation.emailInvalid') }]}
                >
                  <Input
                    disabled={!isEdit || userStore.user?.account === userStore.user?.email}
                    placeholder={t('profile.placeholder.email')}
                  />
                </Form.Item>

                <Form.Item
                  name="mobile"
                  label={<LangText lngKey="common.form.phone" />}
                  rules={[{ pattern: /^[\d*]*$/, message: t('auth.validation.phoneInvalid') }]}
                >
                  <Input
                    disabled={!isEdit || userStore.user?.account === userStore.user?.mobile}
                    placeholder={t('profile.placeholder.phone')}
                  />
                </Form.Item>
              </div>
            </Form>
          </div>
        </section>

        <section className="mt-[42px]">
          <div className="pb-[24px]">
            <LangText lngKey="profile.security.title" className="block text-[28px] font-bold leading-[40px] text-[#333]" />
            <LangText lngKey="profile.security.subtitle" className="mt-[12px] block text-[16px] leading-[22px] text-[#666]" />
          </div>

          <div className="rounded-[20px] bg-white px-[32px] py-[30px]">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-[12px]">
                <div className="flex h-[48px] w-[48px] items-center justify-center rounded-full bg-[#E6F5FF] text-[24px] text-[#328BF8]">
                  <LockOutlined />
                </div>
                <div>
                  <LangText lngKey="profile.security.password.title" className="block text-[20px] font-bold leading-[28px] text-[#333]" />
                  <LangText lngKey="profile.security.password.desc" className="mt-[6px] block text-[16px] leading-[22px] text-[#666]" />
                </div>
              </div>
              <button
                type="button"
                className="h-[42px] min-w-[92px] cursor-pointer rounded-[21px] border border-[#D9D9D9] bg-white px-[24px] text-[18px] text-[#666]"
                onClick={() => setIsEditPassword(true)}
              >
                <LangText lngKey="common.action.modify" />
              </button>
            </div>
          </div>
        </section>
      </div>

      <Modal
        maskClosable
        open={isEditPassword}
        onCancel={() => setIsEditPassword(false)}
        destroyOnHidden
        footer={null}
        closable={false}
        width={560}
        rootClassName="auth-login-modal-root profile-password-modal-root"
      >
        <EditPasswordForm onClose={() => setIsEditPassword(false)} />
      </Modal>
    </div>
  );
};

export default Profile;
